// Consistent ID Generation System for ACCTOO
// This ensures all IDs follow the same pattern across the application

class IDGenerator {
  constructor() {
    this.prefixes = {
      // Device IDs
      DEVICE: 'DEV',
      STABLE_DEVICE: 'STABLE',
      FALLBACK_DEVICE: 'FALLBACK',
      
      // Business IDs
      INVOICE: 'INV',
      QUOTATION: 'QUO',
      PURCHASE: 'PUR',
      PAYMENT: 'PAY',
      CUSTOMER: 'CUST',
      SUPPLIER: 'SUP',
      ITEM: 'ITEM',
      EXPENSE: 'EXP',
      
      // System IDs
      CONFLICT: 'CONF',
      BACKUP: 'BAK',
      SYNC: 'SYNC',
      MIGRATION: 'MIG',
      
      // User IDs
      USER: 'USR',
      COMPANY: 'COMP',
      EMPLOYEE: 'EMP'
    };
    
    this.patterns = {
      // Standard format: PREFIX_TIMESTAMP_HASH
      STANDARD: (prefix, timestamp, hash) => `${prefix}_${timestamp}_${hash}`,
      
      // UUID format: PREFIX_UUID
      UUID: (prefix, uuid) => `${prefix}_${uuid}`,
      
      // Sequential format: PREFIX_YYYY_MM_NUMBER
      SEQUENTIAL: (prefix, year, month, number) => `${prefix}_${year}_${month}_${number.toString().padStart(4, '0')}`,
      
      // Simple format: PREFIX_HASH
      SIMPLE: (prefix, hash) => `${prefix}_${hash}`
    };
  }

  // Generate a stable device ID
  generateDeviceId(type = 'STABLE') {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes[type] || this.prefixes.STABLE_DEVICE;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a business document ID (invoice, quotation, etc.)
  generateDocumentId(type, year = null, month = null) {
    const now = new Date();
    const docYear = year || now.getFullYear();
    const docMonth = month || (now.getMonth() + 1);
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes[type] || 'DOC';
    
    return this.patterns.SEQUENTIAL(prefix, docYear, docMonth, timestamp);
  }

  // Generate a customer/supplier ID
  generatePartyId(type = 'CUSTOMER') {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes[type] || this.prefixes.CUSTOMER;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a payment ID
  generatePaymentId() {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes.PAYMENT;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate an item ID
  generateItemId() {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes.ITEM;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a conflict ID
  generateConflictId(type = 'CONFLICT') {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes[type] || this.prefixes.CONFLICT;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a company ID
  generateCompanyId() {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes.COMPANY;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a user ID
  generateUserId() {
    const timestamp = Date.now().toString(36);
    const hash = this.generateHash();
    const prefix = this.prefixes.USER;
    
    return this.patterns.STANDARD(prefix, timestamp, hash);
  }

  // Generate a UUID-based ID
  generateUUIDId(type = 'ID') {
    const uuid = this.generateUUID();
    const prefix = this.prefixes[type] || 'ID';
    
    return this.patterns.UUID(prefix, uuid);
  }

  // Generate a simple hash-based ID
  generateSimpleId(type = 'ID') {
    const hash = this.generateHash();
    const prefix = this.prefixes[type] || 'ID';
    
    return this.patterns.SIMPLE(prefix, hash);
  }

  // Generate a hash from current context
  generateHash() {
    const context = [
      Date.now().toString(),
      Math.random().toString(36),
      navigator.userAgent,
      window.screen.width,
      window.screen.height
    ].join('|');
    
    let hash = 0;
    for (let i = 0; i < context.length; i++) {
      const char = context.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36).toUpperCase();
  }

  // Generate a UUID v4
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Validate an ID format
  validateId(id, expectedType = null) {
    if (!id || typeof id !== 'string') return false;
    
    // Check if it follows our expected format
    const patterns = [
      /^[A-Z]+_\d+_[A-Z0-9]+$/,  // STANDARD format
      /^[A-Z]+_[A-F0-9-]+$/,      // UUID format
      /^[A-Z]+_\d{4}_\d{2}_\d{4}$/, // SEQUENTIAL format
      /^[A-Z]+_[A-Z0-9]+$/        // SIMPLE format
    ];
    
    const isValidFormat = patterns.some(pattern => pattern.test(id));
    
    if (expectedType) {
      const expectedPrefix = this.prefixes[expectedType];
      return isValidFormat && id.startsWith(expectedPrefix);
    }
    
    return isValidFormat;
  }

  // Get ID type from ID string
  getIdType(id) {
    if (!id || typeof id !== 'string') return null;
    
    const prefix = id.split('_')[0];
    const type = Object.keys(this.prefixes).find(key => this.prefixes[key] === prefix);
    
    return type || 'UNKNOWN';
  }

  // Generate a series number for documents
  generateSeriesNumber(type, year = null, month = null) {
    const now = new Date();
    const docYear = year || now.getFullYear();
    const docMonth = month || (now.getMonth() + 1);
    
    // Get the next number from localStorage or start from 1
    const storageKey = `series_${type}_${docYear}_${docMonth}`;
    const lastNumber = parseInt(localStorage.getItem(storageKey) || '0');
    const nextNumber = lastNumber + 1;
    
    // Store the next number
    localStorage.setItem(storageKey, nextNumber.toString());
    
    return nextNumber;
  }

  // Generate invoice number with series
  generateInvoiceNumber(year = null, month = null) {
    const now = new Date();
    const docYear = year || now.getFullYear();
    const docMonth = month || (now.getMonth() + 1);
    const seriesNumber = this.generateSeriesNumber('INVOICE', docYear, docMonth);
    
    return `INV_${docYear}_${docMonth.toString().padStart(2, '0')}_${seriesNumber.toString().padStart(4, '0')}`;
  }

  // Generate quotation number with series
  generateQuotationNumber(year = null, month = null) {
    const now = new Date();
    const docYear = year || now.getFullYear();
    const docMonth = month || (now.getMonth() + 1);
    const seriesNumber = this.generateSeriesNumber('QUOTATION', docYear, docMonth);
    
    return `QUO_${docYear}_${docMonth.toString().padStart(2, '0')}_${seriesNumber.toString().padStart(4, '0')}`;
  }

  // Generate purchase order number with series
  generatePurchaseOrderNumber(year = null, month = null) {
    const now = new Date();
    const docYear = year || now.getFullYear();
    const docMonth = month || (now.getMonth() + 1);
    const seriesNumber = this.generateSeriesNumber('PURCHASE', docYear, docMonth);
    
    return `PUR_${docYear}_${docMonth.toString().padStart(2, '0')}_${seriesNumber.toString().padStart(4, '0')}`;
  }
}

// Create singleton instance
const idGenerator = new IDGenerator();

// Export individual functions for easy use
export const generateDeviceId = (type) => idGenerator.generateDeviceId(type);
export const generateDocumentId = (type, year, month) => idGenerator.generateDocumentId(type, year, month);
export const generatePartyId = (type) => idGenerator.generatePartyId(type);
export const generatePaymentId = () => idGenerator.generatePaymentId();
export const generateItemId = () => idGenerator.generateItemId();
export const generateConflictId = (type) => idGenerator.generateConflictId(type);
export const generateCompanyId = () => idGenerator.generateCompanyId();
export const generateUserId = () => idGenerator.generateUserId();
export const generateUUIDId = (type) => idGenerator.generateUUIDId(type);
export const generateSimpleId = (type) => idGenerator.generateSimpleId(type);
export const validateId = (id, expectedType) => idGenerator.validateId(id, expectedType);
export const getIdType = (id) => idGenerator.getIdType(id);
export const generateSeriesNumber = (type, year, month) => idGenerator.generateSeriesNumber(type, year, month);
export const generateInvoiceNumber = (year, month) => idGenerator.generateInvoiceNumber(year, month);
export const generateQuotationNumber = (year, month) => idGenerator.generateQuotationNumber(year, month);
export const generatePurchaseOrderNumber = (year, month) => idGenerator.generatePurchaseOrderNumber(year, month);

export default idGenerator;
